import { STRIPE_METADATA_VALUE_MAX, UUID_LENGTH } from './pallet-action-limits'
import { MAX_PALLET_QUANTITY } from './pallet-spec'

/**
 * Stable, machine-readable identifiers for each line item that
 * `pricing.quote` can return. Frontend filtering and the Stripe-charge
 * filter both key off `kind` rather than the human-readable `label`,
 * so labels can be reworded without breaking gating logic.
 *
 * Each kind IS the catalog key of the row it bills — a service's `service_type`
 * (an `order_event_type` value) or a subscription's `storage_location`×`period`. One
 * vocabulary across catalog, quote, and invoice, named after the canonical
 * logistics-event taxonomy. `full_move_delivery` is a catalog product like
 * the rest; its amount is resolved from the territory zone table rather than
 * `product_price` rows (billing strategy `zone_priced`), but it is not
 * a vocabulary exception.
 *
 * Backend builders in `apps/api/src/modules/pricing/pricing.service.ts`
 * stamp every emitted item with one of these values; the Zod schema
 * enforces the contract at the API boundary. `catalogKey`
 * (`apps/api/src/lib/catalog.ts`) maps a product row to its kind.
 *
 * The service values equal the `order_event_type` events. `carrier_move` is the
 * non-fleet 3PL event: it is part of the vocabulary (so catalog, order_event_type,
 * and kind stay aligned) but its product is inactive, so a quote never
 * actually emits it until carrier fulfilment ships.
 */
export const QUOTE_LINE_ITEM_KINDS = [
  'empty_delivery',
  'full_storage_return',
  'full_storage_pickup',
  'empty_retrieval',
  'customer_storage_monthly',
  'customer_storage_weekly',
  'warehouse_storage_monthly',
  // The loaded inter-address orderEvent of a Move flow (FMD). A `full_move_delivery`
  // catalog product exists (billing strategy `zone_priced`); its amount comes
  // from the zone table in `apps/api/src/modules/pricing/move-zones.ts`.
  'full_move_delivery',
  // Non-fleet 3PL move (CM) — the loaded out-of-area leg of a carrier move.
  'carrier_move',
  // Carrier Empty Retrieval (CER) — collecting the empty pallet back from an
  // out-of-area destination after a carrier move. Carrier-handled, not on the
  // fleet board.
  'carrier_empty_retrieval',
  // Upsell add-on (packing kit / protection kit / good) chosen at checkout.
  // Not a logistics event — an `addon` line bills a catalog good/kit and has
  // no order event; the specific product is carried on the line's `productId`.
  'addon',
] as const

export type QuoteLineItemKind = (typeof QUOTE_LINE_ITEM_KINDS)[number]

/**
 * Synthetic quote-line kind for the "buy the pallet" line — NOT a
 * `pricing.quote` output (it's swapped in for `customer_storage_monthly` in buy
 * mode), so it's kept out of `QUOTE_LINE_ITEM_KINDS`. Declared here as the single
 * source so the backend quote surgery and the frontend display filter/type agree.
 */
export const PALLET_PURCHASE_LINE_KIND = 'pallet_purchase' as const
export type PalletPurchaseLineKind = typeof PALLET_PURCHASE_LINE_KIND

/**
 * Canonical Order Event Type taxonomy — the SINGLE source for these values.
 * `@stopal/db` builds the `order_event_type` Postgres enum FROM this array
 * (`pgEnum('order_event_type', ORDER_EVENT_TYPES)`), so the two can't drift and no
 * lockstep test is needed. This leaf package is the shared home because the
 * frontend can import it but can't import `@stopal/db`. Each value equals the
 * `service_type` of the product that bills it.
 */
export const ORDER_EVENT_TYPES = [
  'empty_delivery',
  'full_storage_pickup',
  'full_storage_return',
  'full_move_delivery',
  'empty_retrieval',
  'carrier_move',
  'carrier_empty_retrieval',
] as const

export type OrderEventType = (typeof ORDER_EVENT_TYPES)[number]

/**
 * The carrier-fulfilled subset of the taxonomy — the Order Event Types a
 * carrier area prices (the loaded out-of-area leg + the empty retrieval).
 * Declared once here so the API service, the tRPC input enum, and the web
 * surface all derive from one source rather than hand-copying the union.
 */
export const CARRIER_SERVICE_TYPES = [
  'carrier_move',
  'carrier_empty_retrieval',
] as const satisfies readonly OrderEventType[]

export type CarrierServiceType = (typeof CARRIER_SERVICE_TYPES)[number]

/**
 * Is this Order Event Type one a partner carrier fulfils?
 *
 * The one spelling of the membership test. A type guard, so a caller that
 * narrows keeps the narrower type without a cast.
 */
export function isCarrierServiceType(
  type: OrderEventType,
): type is CarrierServiceType {
  return CARRIER_SERVICE_TYPES.some((carrier) => carrier === type)
}

/**
 * How many collapsed empties occupy one loaded pallet's space. The empty
 * retrieval's whole price rule is this ratio, so the server's fee function and
 * the Ops preview that shows an operator what it will charge both read it here.
 */
export const EMPTIES_PER_LOADED_PALLET = 6

/**
 * Whether each carrier service's price is STORED as its own tier schedule or
 * DERIVED from another one.
 *
 * `satisfies Record<CarrierServiceType, ...>` is the load-bearing token: it is
 * what makes a new taxonomy value fail the build here until someone classifies
 * it. Do not relax it to `Record<string, ...>` — the projection below is a
 * `.filter()`, which forces nothing on its own and would silently drop an
 * unclassified value into neither set.
 *
 * Known limit: only the STORED side is consumed. Classifying a new service as
 * 'derived' compiles clean and no deriver is forced to exist — the sole one,
 * `carrierRetrievalFeeCents`, is written for carrier_empty_retrieval
 * specifically. A second derived service needs a deriver registry here, not
 * just a row in this map.
 */
export const CARRIER_SERVICE_PRICING = {
  carrier_move: 'stored',
  carrier_empty_retrieval: 'derived',
} as const satisfies Record<CarrierServiceType, 'stored' | 'derived'>

export type CarrierPricedServiceType = {
  [K in CarrierServiceType]: (typeof CARRIER_SERVICE_PRICING)[K] extends 'stored'
    ? K
    : never
}[CarrierServiceType]

/** The service types an operator can actually enter a schedule for. */
export const CARRIER_PRICED_SERVICE_TYPES = CARRIER_SERVICE_TYPES.filter(
  (t): t is CarrierPricedServiceType => CARRIER_SERVICE_PRICING[t] === 'stored',
)

/**
 * Pallet-count tiers for a carrier price schedule: '1'..'4' for that exact
 * count, 'extra' for each pallet beyond 4 (same shape as the in-area move
 * `pricing_zone`). Declared once so the DB service, the tRPC input/output
 * schemas, and the web editor all derive from one key list.
 */
export const CARRIER_PRICE_KEYS = ['1', '2', '3', '4', 'extra'] as const

/**
 * How many leading digits of a ZIP make the prefix `carrier_area_zip_prefix` is
 * keyed on. Home is here rather than beside either reader because BOTH the API
 * (`territories.carrier-helpers.ts`) and the release-time zone seed
 * (`packages/db/src/seed-carrier-zones.ts`) compute `left(zip, N)` against the
 * same column, and packages/db cannot import from apps/api — so a literal in
 * one of them is a copy that cannot be kept in step with the other.
 */
export const ZIP_PREFIX_LENGTH = 3

/**
 * What a ZIP3 prefix must look like, derived from the length above rather than
 * typed again — the API's resolver and the client-side serviceability predicate
 * read it, so a length change moves both.
 *
 * ONE READER STILL HOLDS A LITERAL: the Ops paste editor's `/^\d{3}$/`
 * (`territories-list.tsx`). Folding it in costs three lines in a file already
 * over the size ceiling, which the ratchet refuses, so it waits for that file to
 * be split. The cost of the gap is bounded — it validates a draft the server
 * re-checks — but it is a real copy, not a stylistic one.
 */
export const ZIP_PREFIX_RE = new RegExp(`^\\d{${ZIP_PREFIX_LENGTH}}$`)

export type CarrierPriceKey = (typeof CARRIER_PRICE_KEYS)[number]

/** A full tier schedule for one carrier service in one area. */
export type CarrierPriceTiers = Record<CarrierPriceKey, number>

/**
 * How a trip reads to the customer. Not a physical direction — a move both
 * collects and delivers, which is why it is its own value rather than being
 * folded into one of the other two.
 */
export type CustomerTripKind = 'delivery' | 'collection' | 'move'

/**
 * The single per-type attribute registry for the Order Event Type taxonomy.
 * Every surface reads from this — letter code, display label, short ops label,
 * schedulability, and pipeline sequence — so none of them re-declare their own
 * `Record<OrderEventType, …>` map. Surfaces that need extra, surface-specific
 * data (icons, driver action verbs) extend it explicitly rather than re-key.
 */
export interface OrderEventTypeMeta {
  /** Letter code (ED/FSP/FSR/FMD/ER/CM/CER). */
  code: string
  /** Full display label, e.g. "Empty Delivery". */
  label: string
  /** Short operator label for boards/dispatch, e.g. "Deliver empty". */
  opsLabel: string
  /** Whether dispatch can schedule this event (carrier_move can't). */
  schedulable: boolean
  /** Order along the journey pipeline (for sorting an order's events). */
  sequence: number
  /**
   * What the trip does to the pallet in the CUSTOMER's terms — brings it,
   * takes it away, or relocates it. It is what tells a customer-facing
   * sentence which verb is true of a booked trip: "it has to arrive", "we have
   * to collect it", "it has to happen".
   *
   * A move is deliberately its own kind rather than a delivery. It ends at an
   * address, but it STARTS by taking the pallet from wherever it rests — so to
   * a customer whose pallet is sitting in their home, "it has to arrive" is the
   * same class of nonsense this taxonomy exists to prevent.
   *
   * Never rendered. It selects which piece of customer copy to read, so no ops
   * vocabulary leaks out of the registry.
   */
  customerTripKind: CustomerTripKind
}

export const ORDER_EVENT_TYPE_META: Record<OrderEventType, OrderEventTypeMeta> =
  {
    empty_delivery: {
      code: 'ED',
      label: 'Empty Delivery',
      opsLabel: 'Deliver empty',
      schedulable: true,
      sequence: 0,
      customerTripKind: 'delivery',
    },
    full_storage_pickup: {
      code: 'FSP',
      label: 'Full Storage Pickup',
      opsLabel: 'Store pickup',
      schedulable: true,
      sequence: 1,
      customerTripKind: 'collection',
    },
    full_move_delivery: {
      code: 'FMD',
      label: 'Full Move Delivery',
      opsLabel: 'Move pickup',
      schedulable: true,
      sequence: 2,
      customerTripKind: 'move',
    },
    full_storage_return: {
      code: 'FSR',
      label: 'Full Storage Return',
      opsLabel: 'Return to customer',
      schedulable: true,
      sequence: 3,
      customerTripKind: 'delivery',
    },
    empty_retrieval: {
      code: 'ER',
      label: 'Empty Retrieval',
      opsLabel: 'Final pickup',
      schedulable: true,
      sequence: 4,
      customerTripKind: 'collection',
    },
    carrier_move: {
      code: 'CM',
      label: 'Carrier Move',
      opsLabel: 'Carrier move',
      schedulable: false,
      sequence: 5,
      customerTripKind: 'move',
    },
    carrier_empty_retrieval: {
      code: 'CER',
      label: 'Carrier Empty Retrieval',
      opsLabel: 'Carrier retrieval',
      schedulable: false,
      sequence: 6,
      customerTripKind: 'collection',
    },
  }

/** Order Event Types dispatch can schedule — derived from the registry. */
export const SCHEDULABLE_ORDER_EVENT_TYPES = ORDER_EVENT_TYPES.filter(
  (t) => ORDER_EVENT_TYPE_META[t].schedulable,
)

/**
 * Single source of truth for the customer-facing service flows.
 * Backend Zod schema (`pricing.schemas.ts`) and frontend `ServiceFlow`
 * (`apps/web/.../order-flow/types.ts`) both derive from this list, so
 * adding a new flow is a one-edit change with TypeScript surfacing
 * required follow-ups in every consumer.
 */
export const QUOTE_FLOWS = ['store', 'rent', 'move'] as const

export type QuoteFlow = (typeof QUOTE_FLOWS)[number]

/** Narrow an untrusted value — a URL param, a row read as `string` — to a flow. */
export function isQuoteFlow(value: unknown): value is QuoteFlow {
  return QUOTE_FLOWS.some((flow) => flow === value)
}

/**
 * Where a customer's pallets rest while they are in storage.
 *
 * Declared here rather than spelled inline because several doors ask the
 * question and must agree about the answer set: the public quote and both
 * checkout inputs read it.
 *
 * The two Stripe metadata REPLAY shapes in `checkout.service.ts` still spell
 * the literal, and that is a gap rather than a decision: swapping them costs
 * no behaviour change (the value set is identical, and this is about the
 * ANSWER SET, not about the refusal semantics those paths deliberately skip).
 * It was left because that file sits exactly at its file-size baseline and the
 * import would not fit.
 *
 * **Not `@stopal/db`'s `StorageLocation`**, which is the CATALOG's vocabulary
 * for the same idea and spells the customer's own place `'customer'`. This is
 * the WIRE vocabulary, spelled `'my_place'`, and the two must not be swapped:
 * a product row rejects `'my_place'` at the enum, and a quote input rejects
 * `'customer'`. Named `StorageChoice` so an import of the wrong one is a type
 * error rather than a silent mismatch.
 */
export const STORAGE_CHOICES = ['my_place', 'warehouse'] as const

export type StorageChoice = (typeof STORAGE_CHOICES)[number]

/**
 * The `store` flow means the warehouse. The two are ONE choice, not two.
 *
 * A store order resting at the customer's own place quotes no
 * `full_storage_pickup` — it is the `rent` shape wearing a `store` label. Worse,
 * it quoted the on-site monthly rate while `atPlaceBillingFor` started a
 * warehouse subscription unconditionally, so the customer was charged one rate
 * and billed another with nothing anywhere surfacing the difference.
 *
 * Ruled illegal by Marcus rather than reconciled, because reconciling it needs
 * the ORDER to record its storage location and nothing does today: closing it
 * that way would mean a new column plus a backfill that has to assume warehouse
 * for every existing row. Refusing at the door costs no persistence at all.
 */
export const STORAGE_CHOICE_FOR_STORE: StorageChoice = 'warehouse'

/**
 * Whether a flow may rest its pallets at a given location.
 *
 * A PREDICATE, deliberately, rather than a Zod refinement: this module is a
 * zod-free leaf that the web imports, and the rule has three enforcers with
 * three different shapes — two Zod input doors and the order wizard, which
 * DERIVES the location instead of collecting it. One predicate is what keeps
 * those three from drifting; a refinement would serve only the two.
 *
 * **An omitted location is not a violation.** It is defaulted, and for `store`
 * it defaults to the warehouse — which is the direction the original defect ran
 * the other way, `?? 'my_place'` quoting $39 against a $59 subscription. Callers
 * default first and validate the value they arrived at, so an absent field and a
 * present-but-wrong one cannot be answered differently.
 *
 * The Stripe metadata REPLAY shape does not read this, and must not: it parses a
 * value this system already minted and charged for, so refusing there strands a
 * collected charge rather than preventing a bad order. Same reasoning as
 * `months` — see `checkout.service.ts`.
 */
export function isStorageChoiceAllowedForFlow(
  flow: QuoteFlow | null | undefined,
  storage: StorageChoice | null | undefined,
): boolean {
  if (flow !== 'store') return true
  if (storage === null || storage === undefined) return true
  return storage === STORAGE_CHOICE_FOR_STORE
}

/**
 * The location a flow's pallets rest at when the caller named none.
 *
 * `store` defaults to the warehouse; every other flow rests at the customer's
 * place.
 *
 * **One consumer today — the order wizard.** The quote loads the warehouse
 * catalog unconditionally and reads no location at all, so this exists to keep
 * the WIZARD's default from drifting from the rule, not to reconcile two
 * servers. A default that gets `store` backwards quotes $39 against a $59 plan.
 */
export function defaultStorageChoiceForFlow(flow: QuoteFlow): StorageChoice {
  return flow === 'store' ? STORAGE_CHOICE_FOR_STORE : 'my_place'
}

/**
 * The largest prepaid storage window one checkout may buy.
 *
 * **An integrity bound, not a price rule.** A hundred years is far above
 * anything the business would sell, so it can only ever refuse a typo or a
 * crafted payload — what a customer may buy stays a pricing decision, made
 * somewhere else and at a different number.
 *
 * The paid path is bounded only by accident — `qty × months × rate` goes to
 * Stripe, which refuses an amount over 99,999,999 cents — so the ceiling moves
 * with quantity and rate rather than being one number, and the credit-covered
 * path (`insertCreditCoveredOrder`) computes no Stripe amount at all, so it has
 * no ceiling at any quantity. That is how `months` reached the persist
 * unbounded.
 *
 * What that costs lands nowhere near checkout: the column is an `integer`, so a
 * huge value persists cleanly, and at the delivery `firstBillingDate` →
 * `prepaidThrough` → `utcDateOnly` renders a year past 9999 as an expanded year
 * (`"+010433-04"`) that `firstBillingDate` now refuses outright with a
 * `ValidationError` (and that Postgres would refuse on
 * `subscription.next_billing_date` if it ever got that far),
 * failing that stop's delivery advance for good. The customer's read fails more
 * quietly still — `getPalletBilling` returns the same string through a
 * `z.string().nullable()` schema, so the pallet page simply renders a nonsense
 * date. Expect the silent form.
 *
 * **This bounds one operand of a product, and the other is bounded elsewhere.**
 * The date is `scheduledDate + months`, and `z.iso.date()` on its own accepts
 * up to `9999-12-31` while `assertSchedulable` enforces only a floor and the
 * blackout list — so `months: 1` on a December 9999 delivery rendered
 * `"+010000-01"`, the same failure with the month count at its minimum, and no
 * value of this constant could close it. The date end is now bounded by
 * `SCHEDULE_HORIZON_YEARS` on `scheduledDateSchema` (`apps/api/src/lib/
 * schedule-fields.ts`). **Neither ceiling closes the failure mode alone**, and
 * they are only correct together: a delivery at the horizon carrying the full
 * month count must still render a plain date, pinned in
 * `prepaid-months-bound.test.ts`. Raising either one is a change to that pair.
 *
 * Enforced at the input schemas (`checkout.schemas.ts`, `pricing.schemas.ts`),
 * deliberately not in the resolvers below. `quotedStorageMonths` runs inside
 * the post-charge transaction, where a throw means the card was charged and the
 * order died; and a clamp there would record fewer months than the customer
 * paid for, which is the double-charge this pair of functions exists to close.
 *
 * The two confirm paths that read `months` back out of Stripe metadata are
 * likewise unbounded, for the same reason and by two different mechanisms — the
 * hosted-session schema folds an unusable value to a single month through
 * `.catch(undefined)`, while the PI-confirm path parses it bare and relies on
 * `quotedStorageMonths` being total (the argument written where it happens).
 * Both read a value this system minted, so the input bound covers them — for
 * anything minted AFTER it shipped. A Stripe object created before it and
 * confirmed after still carries whatever it was charged for, which is the right
 * outcome: refusing it there would strand a collected charge.
 */
export const MAX_PREPAID_STORAGE_MONTHS = 1200

/**
 * The longest prepaid window the customer-facing picker offers, and therefore
 * the longest one an ordinary checkout can send.
 *
 * It lives here rather than as a default parameter on the picker because two
 * layers have to agree about it and neither can see the other: the duration
 * picker collects it, and `MAX_PREPAID_STORAGE_MONTHS` above must not be set
 * below it. A ceiling the server refuses and the client offers is a checkout
 * that 400s at both the quote and the charge, and an `apps/api` test cannot
 * import a web component to check. Hand-copying the number into that test was
 * tried and moved the failure rather than closing it — the copy silently goes
 * stale in the harmful direction the moment the picker is raised.
 *
 * Same reasoning as `MAX_PALLET_QUANTITY`: a domain ceiling belongs beside the
 * domain, not beside the widget that happens to collect it.
 */
export const LONGEST_OFFERED_STORAGE_MONTHS = 36

/**
 * What ONE serialised `items` entry costs, including the comma joining it to
 * the next — the term `MAX_ORDER_ITEM_LINES` below is derived from.
 *
 * **The skeleton is a hand-transcription of a shape declared in another
 * package** (`_sharedOrderFields.items` in `checkout.schemas.ts`, which
 * `packages/shared` cannot import), so adding a field to that entry widens
 * every serialised entry while this constant does not move. That is the
 * hand-copy `20-architecture.md` forbids, and it is not fixable here — so it is
 * pinned from the one layer that CAN see both: `pallet-quantity-bound.test.ts`
 * measures a real parsed entry against this constant and fails if they differ.
 * Exported for exactly that assertion and nothing else.
 */
export const ORDER_ITEM_SERIALISED_LENGTH =
  '{"containerTypeId":"","quantity":}'.length +
  UUID_LENGTH +
  String(MAX_PALLET_QUANTITY).length +
  // The comma that joins this entry to the next one.
  1

/**
 * The most item lines one checkout may carry.
 *
 * A different bound from `MAX_PALLET_QUANTITY` and derived from a different
 * fact. The quantity ceiling is a domain rule about how many pallets an order
 * may hold; this one is arithmetic on what Stripe will accept, because both
 * charge paths serialise the whole `items` array into ONE metadata value —
 * `metadata.items` on the hosted session and on the saved-card PaymentIntent —
 * and Stripe hard-rejects a value over 500 characters. It never truncates, and
 * neither of those two writes is conditional the way the packed quote is, so an
 * over-long array fails the charge outright at `sessions.create` /
 * `paymentIntents.create`.
 *
 * Derived rather than chosen, for the reason `MAX_ACTION_BATCH_TRIPS` states:
 * the cliff can be computed instead of guessed, so the two paths writing that
 * string cannot disagree about where it is. At today's constants seven entries
 * serialise to 512 characters and six to 439.
 *
 * **The quantity's digit width is conservatism, not sensitivity** — measured
 * across the axis rather than at one point, this answers 6 for every pallet
 * ceiling from 99 up to twelve digits, and only falls to 5 at thirteen. So the
 * `MAX_PALLET_QUANTITY` term cannot be read as a live coupling that a reader
 * must preserve; it keeps the entry honest, and nothing more.
 *
 * The array length is the lower-blast-radius half of this input's exposure —
 * `quantity` is the multiplier that costs memory. Both are equally reachable
 * unauthenticated, through the `publicProcedure` `checkout.previewTax`; what is
 * authenticated is quantity's expensive consequence, not the field.
 */
export const MAX_ORDER_ITEM_LINES = Math.floor(
  // `[` + `]` less the one separator no entry pays for.
  (STRIPE_METADATA_VALUE_MAX - 1) / ORDER_ITEM_SERIALISED_LENGTH,
)

/**
 * The fewest months of warehouse storage we sell.
 *
 * **A real commercial minimum, ruled by Marcus — not a wizard
 * default.** A bound that lives in `apps/web/.../order-flow/types.ts` and is
 * applied only by the browser lets a checkout that is not the wizard — a drifted
 * native shell, a partner integration, a hand-built request — buy a three-month
 * product for one month. The quote and the persist would agree with each other
 * the whole way, so nothing looks wrong and nothing logs.
 *
 * It lives here for the reason `LONGEST_OFFERED_STORAGE_MONTHS` does: a domain
 * bound belongs beside the domain rather than beside the widget that collects
 * it. The wizard reads it from here rather than declaring its own.
 */
export const STORE_MIN_MONTHS = 3

/**
 * The fewest prepaid months a flow may quote. `rent` sells by the month, so its
 * floor is one; `move` charges no monthly storage at all and never asks.
 */
export function storageMonthsFloorForFlow(
  flow: QuoteFlow | null | undefined,
): number {
  return flow === 'store' ? STORE_MIN_MONTHS : 1
}

/**
 * The months a quote charges for — the ONE answer, which the persist derives
 * from rather than deciding again.
 *
 * **`flow` is required, and that is the point.** The floor is per-flow now, so a
 * call site that does not say which flow it is quoting cannot be handed a silent
 * default: the widening would otherwise leave every existing caller answering
 * the OLD question — correct before the minimum existed, so never wrong, so
 * pinned by nothing.
 *
 * Still **total** over `number | null | undefined`, and that remains
 * load-bearing rather than defensive: the PI-confirm path parses `meta.months`
 * and deliberately does not narrow the result, because narrowing there would be
 * the same rule answered twice. Postgres refuses `'NaN'::integer`, so what this
 * prevents is a checkout dying on the persist with the charge already taken.
 *
 * **The floor applies on the replay paths too, and that is deliberate.** They
 * re-resolve through here, and the charge they replay was itself floored, so
 * flooring again is what keeps the recorded block equal to the money taken. The
 * one exposure is a `store` object minted below the minimum BEFORE this shipped
 * and confirmed after: it records three months against one month's payment.
 * That needs a non-wizard checkout inside the minutes between creation and
 * confirm, and the alternative — not flooring here — makes every NEW order
 * record less than it charged, which is the larger error and a permanent one.
 */
export function quotedStorageMonths(
  months: number | null | undefined,
  flow: QuoteFlow | null | undefined,
): number {
  const floor = storageMonthsFloorForFlow(flow)
  if (months === null || months === undefined || !Number.isFinite(months)) {
    return floor
  }
  return Math.max(floor, Math.floor(months))
}

/**
 * What `order.prepaid_storage_months` records: the months the quote charged,
 * or `null` when it charged no monthly storage at all. Two flows charge none —
 * a move prices on-site storage weekly from `daysAtDestination`, and a bought
 * pallet has its prepaid rent line dropped for the purchase price — and for
 * those a recorded month would claim coverage the customer never paid for,
 * deferring the first recurring charge past the paid window.
 *
 * Derived from `quotedStorageMonths` rather than deciding again, so the block
 * recorded on the order cannot disagree with the money taken for it.
 *
 * The flow test is an exhaustive `switch`, not `flow !== 'store' && flow !==
 * 'rent'`. That spelling takes the complement of a hand-listed set over an
 * unguarded `QUOTE_FLOWS`, so a fourth flow that charges monthly storage would
 * land in the null branch — needing no edit here, raising no type error, and
 * silently reinstating exactly the defect this function exists to close.
 * `satisfies never` makes adding a flow fail the build until someone decides.
 *
 * The other no-charge case, a bought pallet, cannot be guarded that way: it is
 * a modifier on `rent`, not a flow, and the line it drops lives in the quote,
 * which this leaf package cannot see. So that one remains a seam to watch —
 * teaching `ORDER_FORM_HIDDEN_KINDS_BY_INTENT` to hide a storage kind, or
 * adding another line-dropping swap beside the pallet purchase, drops the
 * charge and leaves this recording months for it. Extend this function in the
 * same change.
 */
export function prepaidStorageMonthsForOrder(
  flow: QuoteFlow | null | undefined,
  months: number | null | undefined,
  buyingPallet: boolean,
): number | null {
  if (buyingPallet) return null
  if (flow === null || flow === undefined) return null
  switch (flow) {
    case 'store':
    case 'rent':
      return quotedStorageMonths(months, flow)
    case 'move':
      return null
    default:
      // `return flow satisfies never` is the spelling used by the boolean
      // guards in this package, and it is wrong here: it erases to
      // `return flow`, so a value from a caller the types do not police comes
      // back as a STRING from a function declared `number | null` — straight
      // into an integer column, inside the post-charge transaction. Keep the
      // compile-time exhaustiveness, return the safe value at runtime.
      flow satisfies never
      return null
  }
}

/**
 * Per-flow set of line-item kinds that are hidden from the customer
 * order form *and* excluded from the Stripe charge. Implementation
 * detail of `isHiddenForFlow` — kept module-private so callers go
 * through the helper rather than re-implementing filtering logic.
 *
 * No kinds are hidden today; the structure is kept for future per-flow
 * filtering needs.
 */
const ORDER_FORM_HIDDEN_KINDS_BY_INTENT: Record<
  QuoteFlow,
  ReadonlySet<QuoteLineItemKind>
> = {
  store: new Set(),
  move: new Set(),
  rent: new Set(),
}

/**
 * Returns `true` when a line item should be hidden from the order-form
 * display *and* excluded from the Stripe charge for the given flow.
 *
 * Use this on both sides of the order-form boundary so the customer
 * cannot see one total and be charged a different one.
 */
export function isHiddenForFlow(
  flow: QuoteFlow,
  kind: QuoteLineItemKind,
): boolean {
  return ORDER_FORM_HIDDEN_KINDS_BY_INTENT[flow].has(kind)
}

/**
 * Whether an Order Event Type is the customer's MOVE — their loaded box going
 * to a new address, whichever company drives it. Read from the registry's own
 * attribute rather than spelled as a type literal, so a move-shaped type added
 * later inherits every rule built on it. The one predicate: the Journey's
 * visibility rule reads it directly, and the list below derives from it for a
 * SQL `IN (...)` that cannot call a function.
 */
export function isCustomerMoveEvent(type: OrderEventType): boolean {
  return ORDER_EVENT_TYPE_META[type].customerTripKind === 'move'
}

/** `isCustomerMoveEvent` as a list, for the pallet list's move-driven probe. */
export const CUSTOMER_MOVE_EVENT_TYPES: readonly OrderEventType[] =
  ORDER_EVENT_TYPES.filter(isCustomerMoveEvent)
